import { useLocalStorage } from "@rehooks/local-storage";
import { Route, Routes, useNavigate, useParams, useResolvedPath } from "react-router";

import Callout from "ds/components/Callout";
import FeedbackActions from "ds/components/Feedback/Actions";
import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import TierInfo from "components/TierInfo";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useFavicon from "hooks/useFavicon";
import useCanManageStack from "shared/Stack/access/useCanManageStack";
import useCanUpdateStack from "shared/Stack/access/useCanUpdateStack";
import useHasStackAdminAccess from "shared/Stack/access/useHasStackAdminAccess";
import useHasStackWriteAccess from "shared/Stack/access/useHasStackWriteAccess";
import useCanCreateDriftDetection from "shared/Stack/access/useCanCreateDriftDetection";
import useCanUpdateDriftDetection from "shared/Stack/access/useCanUpdateDriftDetection";
import useCanDeleteDriftDetection from "shared/Stack/access/useCanDeleteDriftDetection";
import { BillingTierFeature } from "types/generated";
import { isAnsibleStackVendor } from "utils/stack";
import IacManagementResourceDetails from "views/Account/IacManagement/ResourceDetails";
import Link from "ds/components/Link";

import StackConfigManagement from "./ConfigManagement";
import StackLegacyTerragruntCallout from "./components/LegacyTerragruntCallout";
import { StackContext } from "./Context";
import Contexts from "./Contexts";
import StackDependencies from "./Dependencies";
import StackDependenciesGraph from "./DependenciesGraph";
import Environment from "./Environment";
import Hooks from "./Hooks";
import StackIacManagement from "./IacManagement";
import StackIgnoredRuns from "./IgnoredRuns";
import Notifications from "./Notifications";
import Outputs from "./Outputs";
import StackPolicies from "./Policies";
import PullRequests from "./PullRequests";
import Run from "./Run";
import Runs from "./Runs";
import StackScheduling from "./Scheduling";
import StackSettings from "./Settings";
import StackStateHistory from "./StateHistory";
import Tasks from "./Tasks";
import { ParamTypes, StackGql } from "./Types";
import StackWorkspaces from "./Workspaces";
import { VIEW_STACK_LOCALSTORAGE_KEY } from "./constants";
import { GET_STACK } from "./gql";

const Stack = () => {
  const { stackId } = useParams<ParamTypes>();
  const stackUrl = useResolvedPath("..").pathname;
  const navigate = useNavigate();
  const [configurationManagementViewEnabled, setConfigurationManagementViewEnabled] =
    useLocalStorage(VIEW_STACK_LOCALSTORAGE_KEY, true);

  const handleSetConfigurationManagementViewEnabled = (enabled: boolean) => {
    setConfigurationManagementViewEnabled(enabled);
    if (enabled) {
      navigate("../configuration-management");
    } else {
      navigate("../resources");
    }
  };

  const { data, error, loading } = usePolledQuery<StackGql>(GET_STACK, {
    variables: {
      id: stackId,
    },
    // FYI: `fetchPolicy: "no-cache"` is used to avoid cache collisions,
    // a stack could be partially cached with getting `searchStacks` query on the "Dependencies" tab, etc.
    fetchPolicy: "no-cache",
    pollingGroup: PollingIntervalGroups.Default,
  });

  useFavicon(data?.stack?.state);

  const hasStackAdminAccess = useHasStackAdminAccess()(data?.stack);
  const hasAtLeastWriteAccess = useHasStackWriteAccess()(data?.stack);
  const canUpdateStack = useCanUpdateStack()(data?.stack);
  const canManageStack = useCanManageStack()(data?.stack);
  const canCreateDriftDetection = useCanCreateDriftDetection()(data?.stack);
  const canUpdateDriftDetection = useCanUpdateDriftDetection()(data?.stack);
  const canDeleteDriftDetection = useCanDeleteDriftDetection()(data?.stack);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.stack) {
    return <PageLoading />;
  }

  if (!data?.stack) {
    return <NotFoundPage />;
  }

  const stackContext = {
    // TODO: refactor it, split based on "dynamic" and "static" data or use "selector" pattern
    stack: data.stack,
    stackUrl,
    handleSetConfigurationManagementViewEnabled,
    configurationManagementViewEnabled,
    hasStackAdminAccess,
    canCreateDriftDetection,
    canUpdateDriftDetection,
    canDeleteDriftDetection,
    hasAtLeastWriteAccess,
    canUpdateStack,
    canManageStack,
  };

  return (
    <StackContext.Provider value={stackContext}>
      {data.stack && (
        <>
          {data.stack.workerPool && (
            <TierInfo type="callout" variant="danger" feature={BillingTierFeature.PrivateWorkers}>
              This stack is set up to use a private worker pool, but the current plan does not
              support this functionality. Jobs will not be processed until you upgrade to a plan
              that supports private workers.
            </TierInfo>
          )}

          {data.stack.templateDeployment && (
            <Callout variant="info">
              This stack is managed via Template Deployments. To make changes to its configuration,
              please update the corresponding Deployment.
              <FeedbackActions>
                <Link
                  size="small"
                  href={`/template/${data.stack.templateDeployment?.template.id}/deployment/${data.stack.templateDeployment.id}`}
                >
                  {data.stack.templateDeployment?.name}
                </Link>
              </FeedbackActions>
            </Callout>
          )}

          <StackLegacyTerragruntCallout />

          <Routes>
            <Route index element={<Runs />} />
            <Route path="ignored-runs" element={<StackIgnoredRuns />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="prs" element={<PullRequests />} />
            <Route path="environment" element={<Environment />} />

            {(!configurationManagementViewEnabled || !isAnsibleStackVendor(data.stack)) && (
              <Route path="resources" element={<StackIacManagement />}>
                <Route path="resource/:resourceId" element={<IacManagementResourceDetails />} />
              </Route>
            )}
            {configurationManagementViewEnabled && isAnsibleStackVendor(data.stack) && (
              <Route path="configuration-management" element={<StackConfigManagement />} />
            )}

            <Route path="outputs" element={<Outputs />} />

            <Route path="settings">
              <Route index path="*" element={<StackSettings />} />
            </Route>

            <Route path="hooks" element={<Hooks />} />
            <Route path="contexts" element={<Contexts />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="run/:runId">
              <Route index path="*" element={<Run />} />
            </Route>
            <Route path="dependencies" element={<StackDependencies />} />
            <Route path="dependencies/graph" element={<StackDependenciesGraph />} />
            <Route path="state-history" element={<StackStateHistory />} />
            <Route path="scheduling" element={<StackScheduling />} />
            <Route path="policies" element={<StackPolicies />} />
            <Route path="workspaces" element={<StackWorkspaces />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </>
      )}
    </StackContext.Provider>
  );
};

export default Stack;
